import type { Transaction } from "features/transactions/types";
import type { IRepository, RepositoryAction, RepositoryState } from "../../Repository";

type TransactionColumns = Pick<
    Transaction,
    | 'refId'
    | 'fundStatus'
    | 'status'
    | 'updatedAt'
    | 'settlementStatus'
    | 'settlementStatusDateTime'
    | 'isSettled'
    | 'settledDate'
    | 'localTime'
    | 'transactionDecision'
    | 'transactionRequest'
    | 'transactionResponse'
>;

type HistoryColumns = {
    readonly action: string;
    readonly historyCreatedAt: string;
    readonly dateTime: string;
    readonly _id: string;
};

export const enum Column {
    RefId = 'refId',
    Action = 'action',
    HistoryCreatedAt = 'historyCreatedAt',
    DateTime = 'dateTime',
    Status = 'status',
    FundStatus = 'fundStatus',
    SettlementStatus = 'settlementStatus',
    IsSettled = 'isSettled',
    SettledDate = 'settledDate',
    SettlementStatusDateTime = 'settlementStatusDateTime',
    UpdatedAt = 'updatedAt',
    LocalTime = 'localTime',
    TransactionDecision = 'transactionDecision',
    TransactionRequest = 'transactionRequest',
    TransactionResponse = 'transactionResponse',
    Reason = 'reason',
    ReasonCode = 'reasonCode',
    SucceededAt = 'succeededAt',
    FailedAt = 'failedAt',
    RejectedAt = 'rejectedAt',
    RefundedAt = 'refundedAt',
    ChargedBackAt = 'chargedBackAt',
    FundsWaitingAt = 'fundsWaitingAt',
    FundsReceivedAt = 'fundsReceivedAt',
    FundsMissingAt = 'fundsMissingAt',
    FundsSentAt = 'fundsSentAt',
    FundsPartiallySentAt = 'fundsPartiallySentAt',
    FundsNotExpectedAt = 'fundsNotExpectedAt',
    FundsRefundedAt = 'fundsRefundedAt',
    FundsPartiallyRefundedAt = 'fundsPartiallyRefundedAt',
    FundsPartiallySentRefundedAt = 'fundsPartiallySentRefundedAt',
    FundsPartiallyRefundedSentAt = 'fundsPartiallyRefundedSentAt',
    FundsFullySentAt = 'fundsFullySentAt',
    FundsFullyRefundedAt = 'fundsFullyRefundedAt',
    FundsFullySentRefundedAt = 'fundsFullySentRefundedAt',
    FundsFullyRefundedSentAt = 'fundsFullyRefundedSentAt',
    FundsDeductedAt = 'fundsDeductedAt'
}

export type Comparison = {
    readonly previous: string;
    readonly current: string;
};

export type DiffColumn = keyof TransactionColumns;

export type HistoryRecord =
    & TransactionColumns
    & HistoryColumns
    & {
        readonly differences: {
            readonly [P in DiffColumn]: Comparison;
        };
    };

export type HistoryRepositoryState = RepositoryState<{
    readonly data: Array<HistoryRecord>;
}>;

export type HistoryRepositoryAction = RepositoryAction<
    HistoryRepositoryState
>;

export interface HistoryRepository extends IRepository {
    readonly state: HistoryRepositoryState;

    readonly data: HistoryRepositoryState['data'];

    readonly columns: Array<DiffColumn>;

    readonly fetchHandler: (searchQueryParams?: string) => Promise<HistoryRepositoryState['data']>;
}
